import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const navigate = useNavigate();

  // Set up axios interceptors for auth token
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  useEffect(() => {
    // Check for user data in storage on initial load
    const checkAuth = () => {
      try {
        // Try to get from localStorage first (for remember me)
        const localUser = localStorage.getItem('user');
        const localToken = localStorage.getItem('token');
        
        if (localUser && localToken) {
          setUser(JSON.parse(localUser));
          setAuthToken(localToken);
          setIsAuthenticated(true);
          return;
        }
        
        // If not in localStorage, try sessionStorage
        const sessionUser = sessionStorage.getItem('user');
        const sessionToken = sessionStorage.getItem('token');
        
        if (sessionUser && sessionToken) {
          setUser(JSON.parse(sessionUser));
          setAuthToken(sessionToken);
          setIsAuthenticated(true);
          return;
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        // Clear invalid data
        clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const clearAuthData = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const login = (data, rememberMe = false) => {
    // Normalize role to uppercase to match backend expectations
    if (data.role) {
      data.role = data.role.toUpperCase();
    }
    const { 
      access_token: token, 
      user_id, 
      session_id, 
      role, 
      email, 
      full_name, 
      is_active 
    } = data;
    
    const userData = { 
      id: user_id, 
      email, 
      role, 
      full_name, 
      session_id,
      is_active,
      token 
    };
    
    // Clear any existing data
    clearAuthData();
    
    // Set the default authorization header
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    if (rememberMe) {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(userData));
    }
    
    setUser(userData);
    setAuthToken(token);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    // Create a copy of the token before cleanup
    const token = authToken || localStorage.getItem('token') || sessionStorage.getItem('token');
    
    // Clear client-side auth data first to prevent race conditions
    clearAuthData();
    setUser(null);
    setAuthToken(null);
    setIsAuthenticated(false);
    
    try {
      // Only attempt to call the logout API if we have a token
      if (token) {
        await axios.post(
          `${API_BASE_URL}/auth/logout`,
          {},
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            withCredentials: true,
            // Don't retry on failure to prevent hanging
            'axios-retry': {
              retries: 0
            },
            // Set a timeout to prevent hanging
            timeout: 5000
          }
        );
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Logout API error:', error);
        // Don't show error to user as we've already cleaned up
      }
    } finally {
      // Ensure we navigate to sign-in even if there was an error
      navigate('/sign-in', { replace: true });
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    authToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
